import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container, SectionTitle, SectionSubtitle } from '../../styles/Layout';
import { 
  FaGraduationCap, 
  FaUsers, 
  FaChartLine, 
  FaMobile, 
  FaShieldAlt, 
  FaClock,
  FaMapMarkerAlt,
  FaBell,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaUserCheck,
  FaBook,
  FaArrowRight,
  FaCheck,
  FaCog,
  FaRocket,
  FaDatabase,
  FaCloud,
  FaLaptopCode,
  FaTools,
  FaAward,
  FaLightbulb,
  FaHandshake,
  FaPlay
} from 'react-icons/fa';

const ProductPageContainer = styled.section`
  padding-top: 100px;
  min-height: 100vh;
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%);
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 50%, var(--secondary) 100%);
  color: white;
  padding: var(--spacing-xxxl) 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 25% 25%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(255, 0, 128, 0.1) 0%, transparent 50%);
    z-index: 1;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xxxl);
  align-items: center;
  position: relative;
  z-index: 2;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
    text-align: center;
  }
`;

const HeroContent = styled(motion.div)`
  max-width: 600px;
  
  @media (max-width: 968px) {
    max-width: 100%;
    order: 2;
  }
`;

const HeroVisual = styled(motion.div)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 968px) {
    order: 1;
    margin-bottom: var(--spacing-xl);
  }
`;

const ProductBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(255, 0, 128, 0.1) 100%);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 50px;
  padding: 0.8rem 1.5rem;
  margin-bottom: var(--spacing-lg);
  font-size: 0.9rem;
  color: var(--neon-blue);
  font-weight: 600;
  
  .badge-icon {
    margin-right: 8px;
    color: var(--neon-green);
  }
`;

const ProductName = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  margin-bottom: var(--spacing-md);
  font-weight: 900;
  line-height: 1.1;
  
  .primary-text {
    color: var(--text-primary);
  }
  
  .highlight-text {
    background: linear-gradient(135deg, var(--neon-blue) 0%, var(--neon-magenta) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const ProductTagline = styled(motion.h2)`
  font-size: clamp(1.2rem, 3vw, 1.8rem);
  margin-bottom: var(--spacing-lg);
  color: var(--neon-green);
  font-weight: 600;
`;

const ProductSubheading = styled(motion.p)`
  font-size: clamp(1rem, 2vw, 1.3rem);
  margin-bottom: var(--spacing-xl);
  line-height: 1.7;
  color: var(--text-secondary);
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: var(--spacing-lg);
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, var(--neon-blue) 0%, var(--neon-magenta) 100%);
  color: white;
  padding: 1.3rem 2.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.1rem;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 212, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.6s;
  }
  
  &:hover {
    box-shadow: 0 15px 50px rgba(0, 212, 255, 0.5);
    transform: translateY(-3px);
    
    &::before {
      left: 100%;
    }
    
    svg {
      transform: translateX(5px);
    }
  }
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
`;

const SecondaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  padding: 1.3rem 0;
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--neon-blue);
    text-shadow: 0 0 10px currentColor;
    
    .play-button {
      background: var(--neon-blue);
      box-shadow: 0 0 25px var(--neon-blue);
      transform: scale(1.1);
    }
  }
  
  .play-button {
    width: 55px;
    height: 55px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    backdrop-filter: blur(12px);
    border: 2px solid rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: var(--spacing-md);
    transition: all 0.3s ease;
    
    svg {
      margin-left: 3px;
      font-size: 1.2rem;
    }
  }
`;

const HeroImageContainer = styled.div`
  position: relative;
  width: 500px;
  height: 400px;
  
  @media (max-width: 968px) {
    width: 350px;
    height: 280px;
  }
`;

const OverviewSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(20px);
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xxxl);
  align-items: center;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
  }
`;

const OverviewContent = styled(motion.div)`
  color: var(--text-primary);
  
  h2 {
    font-size: clamp(2rem, 4vw, 3rem);
    margin-bottom: var(--spacing-lg);
    background: linear-gradient(135deg, var(--text-primary) 0%, var(--neon-blue) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.8;
    color: var(--text-secondary);
    margin-bottom: var(--spacing-lg);
  }
`;

const OverviewImage = styled(motion.div)`
  width: 100%;
  height: 400px;
  background: url('https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80') center/cover;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 255, 136, 0.2) 0%, rgba(0, 212, 255, 0.2) 100%);
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    backdrop-filter: blur(2px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    z-index: 2;
  }
`;

const FeaturesSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 30% 40%, rgba(0, 255, 136, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 70% 60%, rgba(139, 92, 246, 0.1) 0%, transparent 50%);
    z-index: 1;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--spacing-xl);
  margin-top: var(--spacing-xl);
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }
`;

const FeatureCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: var(--spacing-xl);
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(135deg, ${props => props.color || 'var(--neon-blue)'} 0%, transparent 100%);
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%);
    transform: translateY(-10px);
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
    border-color: ${props => props.color || 'var(--neon-blue)'};
  }
`;

const FeatureIcon = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 20px;
  background: linear-gradient(135deg, ${props => props.background || 'var(--neon-blue)'} 0%, ${props => props.background || 'var(--neon-blue)'}80 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-lg);
  font-size: 1.8rem;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 10px 30px ${props => props.background || 'var(--neon-blue)'}40;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%);
    border-radius: 20px;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: var(--spacing-lg);
  color: var(--text-primary);
  font-weight: 700;
`;

const FeatureDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: var(--spacing-lg);
  font-size: 1.1rem;
`;

const FeatureList = styled.ul`
  list-style-type: none;
  
  li {
    margin-bottom: var(--spacing-md);
    position: relative;
    padding-left: 30px;
    color: var(--text-secondary);
    line-height: 1.6;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--neon-green) 0%, var(--neon-blue) 100%);
      box-shadow: 0 0 10px var(--neon-green);
    }
  }
`;

const TechStackSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(20px);
`;

const BenefitsSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(20px);
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-xl);
  margin-top: var(--spacing-xl);
`;

const BenefitCard = styled(motion.div)`
  text-align: center;
  padding: var(--spacing-xl);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
    border-color: var(--neon-blue);
  }
`;

const BenefitIcon = styled.div`
  font-size: 3.5rem;
  color: var(--neon-blue);
  margin-bottom: var(--spacing-lg);
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-md);
  color: var(--text-primary);
  font-weight: 700;
`;

const BenefitDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 1.1rem;
`;

const CTASection = styled.section`
  padding: var(--spacing-xxxl) 0;
  background: linear-gradient(135deg, var(--neon-blue) 0%, var(--neon-magenta) 100%);
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 70% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    z-index: 1;
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  margin-bottom: var(--spacing-lg);
  font-weight: 900;
`;

const CTADescription = styled.p`
  font-size: 1.3rem;
  margin-bottom: var(--spacing-xl);
  opacity: 0.9;
  line-height: 1.7;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  background: white;
  color: var(--primary-dark);
  padding: 1.5rem 3rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.2rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    
    svg {
      transform: translateX(5px);
    }
  }
`;

const EduNexusPage = () => {
  const coreFeatures = [
    {
      icon: <FaCog />,
      title: "Master Data Management",
      description: "Comprehensive configuration and setup management for your entire educational ecosystem with intelligent automation.",
      background: "var(--neon-blue)",
      color: "rgba(0, 212, 255, 0.3)",
      features: [
        "Fee structure configuration with flexible pricing models",
        "Academic year setup with automated calendar generation",
        "Class & subject management with curriculum mapping",
        "Expense type configuration with approval workflows",
        "Validation & approval workflows with role-based access",
        "Seamless system integration with third-party tools"
      ]
    },
    {
      icon: <FaShieldAlt />,
      title: "Advanced Security & Access Control",
      description: "Enterprise-grade security with granular access management ensuring complete data privacy and operational efficiency.",
      background: "var(--neon-green)",
      color: "rgba(0, 255, 136, 0.3)",
      features: [
        "Multi-level user permissions with role hierarchy",
        "Department-wise access control with data segregation",
        "Comprehensive audit trail tracking with real-time monitoring",
        "Secure authentication with two-factor authentication",
        "Data encryption at rest and in transit",
        "GDPR compliant data handling and privacy controls"
      ]
    },
    {
      icon: <FaMoneyBillWave />,
      title: "Intelligent Fee Management",
      description: "Automated fee collection and management with AI-powered insights, intelligent tracking and comprehensive reporting.",
      background: "var(--neon-magenta)",
      color: "rgba(255, 0, 128, 0.3)",
      features: [
        "Concession & correction approvals with automated workflows",
        "Automated payment reminders with customizable templates",
        "Comprehensive management reports with visual analytics",
        "Multiple payment gateway support with secure processing",
        "Installment management with flexible payment plans",
        "Late fee calculation with automated penalty application"
      ]
    },
    {
      icon: <FaUserCheck />,
      title: "Smart Attendance Management",
      description: "Advanced attendance tracking with biometric integration, real-time monitoring and intelligent analytics for better insights.",
      background: "var(--neon-purple)",
      color: "rgba(139, 92, 246, 0.3)",
      features: [
        "Daily & subject-wise attendance with detailed tracking",
        "Biometric & RFID support with multiple device integration",
        "Real-time alerts & notifications for parents and staff",
        "Staff attendance management with shift scheduling",
        "Attendance analytics with trend analysis and reporting",
        "Mobile app integration for on-the-go attendance marking"
      ]
    },
    {
      icon: <FaBook />,
      title: "Digital Communication Hub",
      description: "Streamlined communication platform for academic updates, announcements and seamless teacher-parent interaction.",
      background: "var(--neon-yellow)",
      color: "rgba(255, 255, 0, 0.3)",
      features: [
        "Digital homework assignments with multimedia support",
        "Teacher-parent communication with instant messaging",
        "Academic progress updates with detailed performance tracking",
        "Multimedia content support for rich educational content",
        "Announcement system with targeted messaging",
        "Event calendar integration with automated reminders"
      ]
    },
    {
      icon: <FaCalendarAlt />,
      title: "Comprehensive Exam Management",
      description: "Complete examination lifecycle management with automated grading, intelligent scheduling and detailed reporting.",
      background: "var(--neon-blue)",
      color: "rgba(0, 212, 255, 0.3)",
      features: [
        "Exam scheduling & timetables with conflict resolution",
        "Digital marks entry with validation and verification",
        "Customizable grading scales with weighted assessments",
        "Automated report generation with detailed analytics",
        "Question bank management with categorization",
        "Online examination support with proctoring features"
      ]
    }
  ];

  const benefits = [
    {
      icon: <FaRocket />,
      title: "Complete Automation",
      description: "360° educational workflow automation from admission to graduation, eliminating 80% of manual processes and reducing administrative errors by 95%."
    },
    {
      icon: <FaShieldAlt />,
      title: "Enhanced Safety & Security",
      description: "GPS tracking, real-time alerts, biometric access control and comprehensive monitoring ensure student safety and provide complete peace of mind for parents."
    },
    {
      icon: <FaClock />,
      title: "Massive Time Savings",
      description: "Intelligent automation reduces administrative workload by up to 70%, allowing staff to focus on education quality and student development rather than paperwork."
    },
    {
      icon: <FaChartLine />,
      title: "Scalable & Future-Ready",
      description: "Cloud-native architecture that grows with your institution, supporting schools, colleges, and training institutes of all sizes with unlimited scalability."
    },
    {
      icon: <FaAward />,
      title: "Premium User Experience",
      description: "Intuitive, modern interface designed for all user types - from tech-savvy administrators to parents who prefer simple, easy-to-use applications."
    },
    {
      icon: <FaLightbulb />,
      title: "Data-Driven Insights",
      description: "Advanced analytics and reporting provide actionable insights into student performance, financial health, and operational efficiency for better decision making."
    }
  ];

  return (
    <ProductPageContainer>
      <HeroSection>
        <Container>
          <HeroGrid>
            <HeroContent
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <ProductBadge
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <FaAward className="badge-icon" />
                Next-Generation Educational ERP Solution
              </ProductBadge>
              
              <ProductName
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <span className="primary-text">EduNexus</span>
                <span className="highlight-text">Smart Campus ERP</span>
              </ProductName>
              
              <ProductTagline
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Revolutionizing Education Management with AI-Powered Intelligence
              </ProductTagline>
              
              <ProductSubheading
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                A comprehensive Enterprise Resource Planning solution that transforms educational institutions through intelligent automation, seamless communication, and data-driven insights. Experience the future of education management.
              </ProductSubheading>
              
              <HeroButtons
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <PrimaryButton 
                  href="#contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Schedule Demo <FaArrowRight />
                </PrimaryButton>
                
                <SecondaryButton 
                  href="#features"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="play-button">
                    <FaPlay />
                  </div>
                  Explore Features
                </SecondaryButton>
              </HeroButtons>
            </HeroContent>

            <HeroVisual
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
            </HeroVisual>
          </HeroGrid>
        </Container>
      </HeroSection>

      <OverviewSection>
        <Container>
          <OverviewGrid>
            <OverviewContent
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2>Transforming Educational Excellence</h2>
              <p>
                EduNexus is a revolutionary, AI-enabled, cloud-based Enterprise Resource Planning system specifically designed for modern educational institutions. Our platform seamlessly integrates all aspects of educational operations into a unified, intelligent ecosystem.
              </p>
              <p>
                Built on cutting-edge cloud architecture with advanced security protocols, EduNexus transforms traditional education management through intelligent automation, predictive analytics, and seamless user experiences across web and mobile platforms.
              </p>
              <p>
                From student enrollment and academic planning to financial management and parent communication, EduNexus provides a comprehensive digital transformation solution that empowers educational institutions to focus on what matters most - delivering exceptional education and fostering student success.
              </p>
            </OverviewContent>
            
            <OverviewImage
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              animate={{
                y: [0, -15, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </OverviewGrid>
        </Container>
      </OverviewSection>

      <FeaturesSection id="features">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionTitle style={{ color: 'white', textAlign: 'center' }}>
              Comprehensive Feature Suite
            </SectionTitle>
            <SectionSubtitle $dark style={{ textAlign: 'center' }}>
              Powerful modules designed to address every aspect of modern educational institution management with intelligent automation and seamless integration.
            </SectionSubtitle>
          </motion.div>
          
          <FeaturesGrid>
            {coreFeatures.map((feature, index) => (
              <FeatureCard
                key={index}
                color={feature.color}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <FeatureIcon background={feature.background}>
                  {feature.icon}
                </FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
                <FeatureList>
                  {feature.features.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </FeatureList>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Container>
      </FeaturesSection>

      <BenefitsSection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionTitle style={{ color: 'white', textAlign: 'center' }}>
              Why Choose EduNexus?
            </SectionTitle>
            <SectionSubtitle $dark style={{ textAlign: 'center' }}>
              Transform your educational institution with intelligent automation, enhanced operational efficiency, and data-driven insights that drive student success and institutional growth.
            </SectionSubtitle>
          </motion.div>
          
          <BenefitsGrid>
            {benefits.map((benefit, index) => (
              <BenefitCard
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <BenefitIcon>{benefit.icon}</BenefitIcon>
                <BenefitTitle>{benefit.title}</BenefitTitle>
                <BenefitDescription>{benefit.description}</BenefitDescription>
              </BenefitCard>
            ))}
          </BenefitsGrid>
        </Container>
      </BenefitsSection>

      <CTASection>
        <Container>
          <CTAContent>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <CTATitle>Ready to Transform Your Educational Institution?</CTATitle>
              <CTADescription>
                Join the revolution in education management. Let's build a smarter, safer, and more efficient learning ecosystem together. Schedule your personalized demo today and discover how EduNexus can revolutionize your institution's operations.
              </CTADescription>
              <CTAButton href="#contact">
                Schedule Your Demo Today <FaArrowRight />
              </CTAButton>
            </motion.div>
          </CTAContent>
        </Container>
      </CTASection>
    </ProductPageContainer>
  );
};

export default EduNexusPage;